import React from 'react';
import { useUser } from './UserProvider';
import { LiftStatusStorage } from 'Utils/Storage';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import { PageLoader } from 'Router/Routes';
import { $api, unwrap } from '~/lib/api-client';

//
const SelectedOrganiationContext = React.createContext<{
  selectedOrganization: LiftStatus.Schemas.IOrganisation | null;
  setSelectedOrganizationId: (value: number | null) => void;
  organizations: LiftStatus.Schemas.IOrganisation[];
} | null>(null);

const SelectedOrganizationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();

  const user = useUser();

  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<
    number | null
  >(() => LiftStatusStorage.organisationId ?? user.organisation_id);

  const updateOrganizationId = (value: number | null) => {
    setSelectedOrganizationId(value);
    LiftStatusStorage.organisationId = value;

    if (!value) {
      // No org selected anymore so navigate to /dashboard which shows a select organization message
      navigate('/dashboard');
    }
  };

  const organizationsQuery = useQuery({
    queryKey: [
      'liftstatus',
      'organisation',
      {
        limit: '1000',
      },
    ],
    queryFn: () =>
      unwrap(
        $api['/liftstatus/organisation'].GET({
          params: { query: { limit: 1000 } },
        })
      ),
  });

  return match(organizationsQuery)
    .with({ status: 'pending' }, () => <PageLoader />)
    .with({ status: 'error' }, ({ error }) => <p>{error.message}</p>)
    .with({ status: 'success' }, ({ data: { items: organizations } }) => {
      // TODO: enable strict null checks for typescript
      // Because it is disabled ts thinks .find() always returns a value
      const selectedOrganization = selectedOrganizationId
        ? (organizationsQuery.data?.items.find(
            (org) => org.id === selectedOrganizationId
          ) ?? null)
        : null;

      return (
        <SelectedOrganiationContext.Provider
          value={{
            selectedOrganization,
            setSelectedOrganizationId: updateOrganizationId,
            organizations,
          }}
        >
          {children}
        </SelectedOrganiationContext.Provider>
      );
    })
    .exhaustive();
};

const useSelectedOrganization = () => {
  const context = React.useContext(SelectedOrganiationContext);

  if (!context) {
    throw new Error(
      'useSelectedOrganization must be used within a SelectedOrganizationProvider'
    );
  }

  return context;
};

export { SelectedOrganizationProvider, useSelectedOrganization };
